<template>
  <DashboardCard :border="false" :title="$t('widgets.business_units.title')" :firstContent="true">
    <template v-slot:content>
      <div
          style="width: 200px; height: 200px;"
          class="p-2 mx-auto border rounded-circle shadow-circle"
      >
        <Doughnut :chart-data="chartData" :chart-options="chartOptions" :plugins="[centerTextPlugin]" />
      </div>
    </template>

    <template v-slot:table>
      <div class="table-container mt-4">
        <MDBTable borderless class="table-dashboard mx-auto p-0">
          <tbody>
          <tr
              v-for="(item, i) in dynamicUnits"
              :key="item.label"
              class="border fs-12px"
          >
            <td>
            <span
                class="legend-circle"
                :style="{ backgroundColor: colors[i % colors.length] }"
            ></span>
              {{ item.label }}
            </td>
            <td>{{ item.percent }}</td>
          </tr>
          </tbody>
        </MDBTable>
      </div>
    </template>
  </DashboardCard>
</template>

<script setup>
import { computed, defineProps } from "vue";
import DashboardCard from "../User/Wholesaler/DashboardCard.vue";
import { MDBTable } from "mdb-vue-ui-kit";
import { Doughnut } from "vue-chartjs";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { createCenterTextPlugin } from "../../utils/chartPlugins";
import { useI18n } from "vue-i18n";

ChartJS.register(Title, Tooltip, Legend, ArcElement);

const props = defineProps({
  data: { type: Object, required: true },
});

const { t } = useI18n();

const colors = [
  "#F55B5E",
  "#199EC3",
  "#51C880",
  "#58595B",
  "#FFBA3B",
  "#AF7AC5",
  "#48C9B0",
  "#F8C471",
  "#EC7063",
  "#5DADE2",
];

const dynamicUnits = computed(() => {
  const obj = props.data || {};
  const entries = Object.entries(obj)
      .filter(([key, value]) => key !== "signature" && value && typeof value === "object");

  return entries.map(([label, value]) => ({
    label,
    count: value.uses_count ?? 0,
    percent: value.uses_count_percent ?? 0,
  }));
});

const chartData = computed(() => ({
  labels: dynamicUnits.value.map(u => u.label),
  datasets: [
    {
      data: dynamicUnits.value.map(u => u.count),
      backgroundColor: dynamicUnits.value.map((_, i) => colors[i % colors.length]),
      borderColor: "#fff",
      borderWidth: 2,
      spacing: 3,
    },
  ],
}));

const chartOptions = computed(() => ({
  responsive: true,
  cutout: "70%",
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: (ctx) => {
          const unit = dynamicUnits.value[ctx.dataIndex];
          return ` ${unit.label}: ${unit.count} (${unit.percent}%)`;
        },
      },
    },
  },
}));

const centerTextPlugin = createCenterTextPlugin(
    t("widgets.business_units.centerTitle"),
    { color: "#000", weight: "700", divisor: 15 }
);
</script>


<style lang="scss">
@import "@/styles/_variables.scss";
@import "@/styles/table_dashboard.scss";

.table-container {
  max-height: calc(5 * 30px);
  overflow-y: auto;
  position: relative;

  &::after {
    display: block;
    content: "";
    position: sticky;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 30px;
    pointer-events: none;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1));
  }
}

.table-container tbody tr {
  height: 20px;
}

.legend-circle {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 8px;
}

.table-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-container::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 100px;
}

.table-container::-webkit-scrollbar-thumb {
  background-image:
      linear-gradient(
              to right,
              transparent calc(50% - 1.5px),
              #CBCBCB calc(50% - 1.5px),
              #CBCBCB calc(50% + 1.5px),
              transparent calc(50% + 1.5px)
      );
  background-repeat: no-repeat;
  background-position: center;
  background-size: 100% 100%;
  border-radius: 100px;
  min-height: 56px;
  transition: background-image 0.2s ease;
}

.table-container::-webkit-scrollbar-thumb:hover {
  background-image:
      linear-gradient(
              to right,
              transparent calc(50% - 1.5px),
              #58595B calc(50% - 1.5px),
              #58595B calc(50% + 1.5px),
              transparent calc(50% + 1.5px)
      );
}

.table-container::-webkit-scrollbar-button {
  width: 8px;
  height: 4px;
  background: transparent;
  border: none;
  display: block;
}

.table-container::-webkit-scrollbar-button:single-button:vertical:decrement {
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-bottom: 3.9px solid #CBCBCB;
  margin: auto;
}

.table-container::-webkit-scrollbar-button:single-button:vertical:increment {
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 3.9px solid #CBCBCB;
  margin: auto;
}

</style>

